import React, { ReactNode, useEffect } from 'react';

interface ModalProps {
  children: ReactNode;
  onClose: () => void;
  title?: string;
  size?: 'small' | 'medium' | 'large' | 'full';
}

const Modal: React.FC<ModalProps> = ({ 
  children, 
  onClose, 
  title, 
  size = 'medium' 
}) => {
  // Close modal on Escape key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
          size === 'full' ? '' : 'pointer-events-none'
        }`}
      >
        <div 
          className={`
            bg-white rounded-lg shadow-xl pointer-events-auto
            ${size === 'small' ? 'max-w-md' : 
              size === 'medium' ? 'max-w-2xl' : 
              size === 'large' ? 'max-w-4xl' : 
              'w-full h-full'
            }
            ${size === 'full' ? '' : 'max-h-[90vh]'}
            w-full
            overflow-hidden
            transform transition-all
          `}
        >
          {/* Header */}
          {title && (
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg 
                    className="h-6 w-6" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M6 18L18 6M6 6l12 12" 
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          <div className={`
            ${size === 'full' ? 'h-full' : 'overflow-y-auto'}
            ${title ? '' : 'pt-4'}
          `}>
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

export default Modal;
